import React, { useEffect, useMemo, useState } from "react";
import { Button } from "@mui/material";
import BoltOutlinedIcon from "@mui/icons-material/BoltOutlined";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import { off, onValue, ref } from "firebase/database";
import { useParams } from "react-router-dom";

import { database } from "../../firebase";
import MetricCard from "../ui/MetricCard";
import RecordDialog from "../ui/RecordDialog";
import EmptyState from "../ui/EmptyState";
import Chart from "../charts/Chart";
import { baseChartOption, lineSeries, metricColor, withAlpha } from "../charts/chartTheme";
import {
  EM_DASH,
  describe,
  formatNumber,
  formatRelative,
  formatTime,
  formatTimestamp,
  monthlyAverage,
} from "../../utils/format";

/* The device has used several key names for this reading over firmware
   revisions; take the first that carries a finite number. */
const VALUE_KEYS = [
  "electrodermalActivity",
  "eda",
  "edaAverage",
  "value",
  "reading",
  "microsiemens",
];

const readValue = (record = {}) => {
  for (const key of VALUE_KEYS) {
    const value = Number(record[key]);
    if (Number.isFinite(value)) return value;
  }
  return null;
};

const ElectrodermalActivity = ({ title = "Electrodermal activity", extraActions, menu }) => {
  const { watchId } = useParams();
  const [records, setRecords] = useState([]);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    const nodeRef = ref(database, `Health/${watchId}/electrodermalActivity`);

    const handler = onValue(nodeRef, (snapshot) => {
      if (!snapshot.exists()) {
        setRecords([]);
        setSelectedRecord(null);
        return;
      }

      const parsed = Object.entries(snapshot.val() || {})
        .map(([key, record]) => {
          const timestamp = Number(record?.timestamp ?? key);
          const value = readValue(record);
          if (!Number.isFinite(timestamp) || !Number.isFinite(value)) return null;
          return { ...record, id: key, timestamp, value };
        })
        .filter(Boolean)
        .sort((a, b) => b.timestamp - a.timestamp);

      setRecords(parsed);
      setSelectedRecord((previous) => {
        if (!previous) return parsed[0] ?? null;
        return parsed.find((record) => record.id === previous.id) ?? parsed[0] ?? null;
      });
    });

    return () => off(nodeRef, "value", handler);
  }, [watchId]);

  const series = useMemo(
    () => records.slice().sort((a, b) => a.timestamp - b.timestamp),
    [records]
  );
  const values = useMemo(() => series.map((r) => r.value), [series]);
  const stats = useMemo(() => describe(values, 2), [values]);

  const accent = metricColor("eda");
  const selectedIndex = selectedRecord
    ? series.findIndex((r) => r.id === selectedRecord.id)
    : -1;

  const buildOption = () => {
    const color = metricColor("eda");
    const line = lineSeries({
      name: "Electrodermal activity",
      data: values,
      color,
      showSymbol: values.length <= 40,
    });

    if (selectedIndex >= 0) {
      line.markPoint = {
        symbol: "circle",
        symbolSize: 11,
        itemStyle: { color, borderColor: "#fff", borderWidth: 2 },
        label: { show: false },
        data: [{ coord: [selectedIndex, values[selectedIndex]] }],
      };
      line.markLine = {
        silent: true,
        symbol: "none",
        lineStyle: { color: withAlpha(color, 0.4), type: [4, 4], width: 1 },
        label: { show: false },
        data: [{ xAxis: selectedIndex }],
      };
    }

    return baseChartOption({
      categories: series.map((r) => formatTime(r.timestamp)),
      series: [line],
      yName: "µS",
      tooltipFormatter: (params) => {
        const point = params?.[0];
        if (!point) return "";
        return `${point.axisValue}<br/><strong>${point.value} µS</strong>`;
      },
    });
  };

  return (
    <>
      <MetricCard
        accent={accent}
        icon={<BoltOutlinedIcon />}
        title={title}
        meta={
          <>
            <span className="wg-chip wg-chip--accent">
              {selectedRecord ? "Record loaded" : "No records"}
            </span>
            {selectedRecord && (
              <span className="wg-chip">{formatRelative(selectedRecord.timestamp)}</span>
            )}
          </>
        }
        menu={menu}
        actions={
          <>
            {extraActions}
            <Button
              size="small"
              startIcon={<HistoryOutlinedIcon />}
              onClick={() => setSelectedRecord(records[0] ?? null)}
              disabled={!records.length}
            >
              Latest
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={() => setDialogOpen(true)}
              disabled={!records.length}
            >
              Select record
            </Button>
          </>
        }
        stats={[
          {
            label: "Selected",
            value: selectedRecord ? formatNumber(selectedRecord.value, 2) : EM_DASH,
            unit: "µS",
            meta: selectedRecord?.displayName || "Unassigned",
          },
          { label: "Average", value: stats.avg, unit: "µS", meta: `${stats.count} samples` },
          { label: "Range", value: stats.range, unit: "µS", meta: "Observed window" },
          {
            label: "This month",
            value: monthlyAverage(records, (r) => r.value, 2),
            unit: "µS",
            meta: `${records.length} records`,
          },
        ]}
        empty={
          !series.length ? (
            <EmptyState
              icon={<BoltOutlinedIcon />}
              title="Waiting for electrodermal activity"
              copy="No measurements have been captured yet. Request one from the watch to populate this chart."
            />
          ) : null
        }
      >
        <Chart buildOption={buildOption} height={300} deps={[values, selectedIndex]} />
      </MetricCard>

      <RecordDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSelect={(record) => {
          setSelectedRecord(record);
          setDialogOpen(false);
        }}
        records={records}
        accent={accent}
        title="Electrodermal records"
        getKey={(record) => record.id}
        getTitle={(record) => `${formatNumber(record.value, 2)} µS`}
        getMeta={(record) =>
          `${formatTimestamp(record.timestamp)} · ${formatRelative(record.timestamp)}`
        }
        isSelected={(record) => selectedRecord?.id === record.id}
      />
    </>
  );
};

export default ElectrodermalActivity;
